import type { RouteName } from '@/router/route-definitions'
import { routePaths, routeRequiresParams, type RouteParamsByName } from '@/router/route-utils'
import type { NavigateOptions } from 'react-router'
import { useNavigate } from 'react-router'

/**
 * Type-safe navigation hook that works with the unified route definitions
 *
 * @example
 * const navigate = useTypedNavigate()
 *
 * // Static routes
 * navigate('home')
 * navigate('profile', { replace: true })
 *
 * // Dynamic routes with parameters
 * navigate('campaign.detail', { id: '123' })
 * navigate('campaign.detail', { id: '123' }, { state: { from: 'home' } })
 */
export function useTypedNavigate() {
  const navigate = useNavigate()

  // Overloaded function for better type inference
  function typedNavigate<K extends RouteName>(
    routeName: K,
    ...args: RouteParamsByName<K> extends Record<string, never>
      ? [options?: NavigateOptions]
      : [params: RouteParamsByName<K>, options?: NavigateOptions]
  ): void {
    const requiresParams = routeRequiresParams(routeName)

    if (
      requiresParams &&
      args.length > 0 &&
      typeof args[0] === 'object' &&
      !('replace' in args[0])
    ) {
      // Dynamic route with parameters
      const params = args[0] as RouteParamsByName<K>
      const options = args[1] as NavigateOptions | undefined
      const path = routePaths[routeName](params)
      navigate(path, options)
    } else {
      // Static route
      const options = args[0] as NavigateOptions | undefined
      const path = routePaths[routeName]()
      navigate(path, options)
    }
  }

  return typedNavigate
}

/**
 * Alternative hook that returns an object with typed navigation methods
 * This provides autocomplete for all available routes
 *
 * @example
 * const nav = useTypedNavigation()
 * nav.home()
 * nav.campaignDetail({ id: '123' })
 */
export function useTypedNavigation() {
  const navigate = useNavigate()

  return {
    // Main pages
    home: (options?: NavigateOptions) => navigate(routePaths.home(), options),

    // Campaign
    campaign: (options?: NavigateOptions) => navigate(routePaths.campaign(), options),

    campaignDetail: (params: { id: string }, options?: NavigateOptions) =>
      navigate(routePaths['campaign.detail'](params), options),

    // Applied & Media
    applied: (options?: NavigateOptions) => navigate(routePaths.applied(), options),

    media: (options?: NavigateOptions) => navigate(routePaths.media(), options),

    // Profile routes
    profile: (options?: NavigateOptions) => navigate(routePaths.profile(), options),

    myAccount: (options?: NavigateOptions) => navigate(routePaths['profile.myAccount'](), options),

    editAccount: (options?: NavigateOptions) =>
      navigate(routePaths['profile.myAccount.editAccount'](), options),

    editAddress: (options?: NavigateOptions) =>
      navigate(routePaths['profile.myAccount.editAddress'](), options),

    myBank: (options?: NavigateOptions) => navigate(routePaths['profile.myBank'](), options),

    editBank: (options?: NavigateOptions) => navigate(routePaths['profile.editBank'](), options),

    editPassword: (options?: NavigateOptions) =>
      navigate(routePaths['profile.editPassword'](), options),

    // Auth routes
    signin: (options?: NavigateOptions) => navigate(routePaths['auth.signin'](), options),

    signup: (options?: NavigateOptions) => navigate(routePaths['auth.signup'](), options),

    signupComplete: (options?: NavigateOptions) =>
      navigate(routePaths['auth.signupComplete'](), options),

    lineValidation: (options?: NavigateOptions) =>
      navigate(routePaths['auth.lineValidation'](), options),

    findEmail: (options?: NavigateOptions) => navigate(routePaths['auth.findEmail'](), options),

    resetPassword: (options?: NavigateOptions) =>
      navigate(routePaths['auth.resetPassword'](), options),

    resetPasswordLineAuth: (options?: NavigateOptions) =>
      navigate(routePaths['auth.resetPasswordLineAuth'](), options),

    resetPasswordLineQR: (options?: NavigateOptions) =>
      navigate(routePaths['auth.resetPasswordLineQR'](), options),

    resetPasswordForm: (options?: NavigateOptions) =>
      navigate(routePaths['auth.resetPasswordForm'](), options),
  }
}
